<template>
    <nav class="navbar navbar-expand-md navbar-light bg-light">
        <div class="container-fluid">
            <a class="navbar-brand" href="#">
                <img class="seal" alt="GCDP Seal" src="@/assets/GCDP Seal.svg">
            </a>
            <button class="navbar-toggler" type="button" aria-controls="navbarNav" aria-expanded="false"
                aria-label="Toggle navigation" @click="navLightVisible = !navLightVisible">
                <span class="navbar-toggler-icon"></span>
            </button>
            <div class="navbar-collapse" :class="!navLightVisible ? 'collapse' : ''" id="navbarNav"
                :key="currentUserRoles">
                <ul class="navbar-nav">

                    <!-- Home Link -->
                    <li class="nav-item">
                        <router-link class="nav-link" to="/" @click="handleNavLinkClick">Home</router-link>
                    </li>

                    <!-- Captains Link (public) -->
                    <li class="nav-item">
                        <router-link class="nav-link" :to="{ name: 'captains' }"
                            @click="handleNavLinkClick">Captains</router-link>
                    </li>

                    <!-- Maps Link -->
                    <li class="nav-item" v-if="currentUserRoles.includes('read_maps')" :key="currentUserRoles">
                        <router-link class="nav-link" @click="handleNavLinkClick"
                            :to="{ name: 'maps', params: { state: countyStore.countyState, county: countyStore.county } }">Maps</router-link>
                    </li>

                    <!-- Precincts Dropdown -->
                    <BNavItemDropdown v-if="currentUserRoles.includes('read_reports')" text="Precincts"
                        :key="currentUserRoles" class="wider-dropdown" menu-class="wider-dropdown-menu">
                        <PrecinctListComponent :currentUserPreferences="currentUserPreferences"
                            :currentUserRoles="currentUserRoles" />
                    </BNavItemDropdown>

                    <!-- Membership Link -->
                    <li class="nav-item" v-if="currentUserRoles.includes('read_memberships')" :key="currentUserRoles">
                        <router-link class="nav-link"
                            :to="{ name: 'membership', params: { state: countyStore.countyState, county: countyStore.county } }"
                            @click="handleNavLinkClick">Membership</router-link>
                    </li>

                    <!-- Contributions Link -->
                    <li class="nav-item" v-if="currentUserRoles.includes('read_contributions')" :key="currentUserRoles">
                        <router-link class="nav-link"
                            :to="{ name: 'contributions', params: { state: countyStore.countyState, county: countyStore.county } }"
                            @click="handleNavLinkClick">
                            Contributions
                        </router-link>
                    </li>

                    <!-- Volunteer Search Link -->
                    <li class="nav-item" v-if="currentUserRoles.includes('read_volunteers')" :key="currentUserRoles">
                        <router-link class="nav-link" :to="{ name: 'volunteer-search' }" @click="handleNavLinkClick">
                            Volunteers
                        </router-link>
                    </li>
                </ul>

                <!-- Contact Search Dropdown -->
                <ContactSearchDropdownComponent :currentUserPreferences="currentUserPreferences"
                    :currentUserRoles="currentUserRoles" v-if="currentUserRoles.includes('read_reports')">
                </ContactSearchDropdownComponent>

                <!-- Right hand side menus -->
                <ul class="navbar-nav ms-auto">
                    <!-- Data Dropdown -->
                    <BNavItemDropdown v-if="currentUserRoles.includes('upload_data')" :key="currentUserRoles" right
                        title="Data Dropdown">
                        <template #button-content>
                            <font-awesome-icon icon="fa-solid fa-database" title="Data" />
                        </template>
                        <!-- Matches -->
                        <BDropdownItem v-if="currentUserRoles.includes('read_match_reports')" :key="currentUserRoles"
                            to="/matches" @click="handleNavLinkClick">Contact Matches</BDropdownItem>
                        <BDropdownItem to="/data" @click="handleNavLinkClick">Data Files</BDropdownItem>
                        <BDropdownItem to="/tasks" @click="handleNavLinkClick">Task Manager</BDropdownItem>
                    </BNavItemDropdown>
                    <!-- Settings Dropdown -->
                    <BNavItemDropdown v-if="currentUserRoles.includes('admin')" :key="currentUserRoles" right
                        title="Settings">
                        <template #button-content>
                            <i class="bi bi-gear-fill"></i>
                        </template>
                        <BDropdownItem to="/admin/role-editor" @click="handleNavLinkClick">Role Editor</BDropdownItem>
                        <BDropdownItem to="/admin/new-users" @click="handleNavLinkClick">New Users</BDropdownItem>
                        <BDropdownItem to="/admin/mail-browser" @click="handleNavLinkClick">Mail Browser</BDropdownItem>
                        <BDropdownItem to="/admin/impersonate" @click="handleNavLinkClick">Impersonation</BDropdownItem>
                        <BDropdownItem to="/admin/volunteer-interests" @click="handleNavLinkClick">Volunteer Interests
                        </BDropdownItem>
                        <BDropdownItem
                            :to="{ name: 'precinct-sorter', params: { state: countyStore.countyState, county: countyStore.county } }"
                            @click="handleNavLinkClick">Precinct Sorter</BDropdownItem>
                    </BNavItemDropdown>
                    <!-- County Dropdown -->
                    <BNavItemDropdown :text="`${countyStore.county} (${countyStore.countyState})`" right
                        title="County Selection">
                        <BDropdownItem v-for="newCounty in countyStore.counties" :key="newCounty"
                            :class="{ active: newCounty == countyStore.county }" href="#"
                            @click="setCounty(newCounty); handleNavLinkClick()">
                            {{ newCounty }} ({{ countyStore.getCountyState(newCounty) }})
                        </BDropdownItem>
                    </BNavItemDropdown>
                    <!-- User Name or Login Option -->
                    <BNavItemDropdown v-if="isLoggedIn" :key="isLoggedIn" right
                        :title="user?.displayName || user?.email">
                        <template #button-content>
                            {{ user?.displayName || user?.email }}
                            <span v-if="hasIncompleteForms" class="badge bg-warning text-dark ms-2">!</span>
                        </template>
                        <BDropdownItem href="#" @click="navLightVisible = false; showPreferencesModal()">
                            My Preferences
                        </BDropdownItem>
                        <BDropdownItem to="/member-profile" @click="handleNavLinkClick">
                            My Profile
                            <span v-if="hasIncompleteForms" class="badge bg-warning text-dark ms-2">Please Update</span>
                        </BDropdownItem>
                        <BDropdownItem href="#" @click="clickSignOut">Logout</BDropdownItem>
                    </BNavItemDropdown>
                    <BNavItemDropdown v-else text="Login" right title="Login">
                        <BDropdownItem href="#" @click="signInWithGoogle">Login with Google</BDropdownItem>
                        <!-- email login is going to require more work. -->
                        <BDropdownItem to="/login" @click="handleNavLinkClick">Login</BDropdownItem>
                    </BNavItemDropdown>
                </ul>
            </div>
        </div>
    </nav>
    <PreferencesModal ref="preferencesModal" :auth="auth" />
</template>

<script>
import { onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import { createRolesDocument, clickSignOut, signInWithGoogle } from '@/lib/authentication.js'
import { useRouter } from 'vue-router'
import PreferencesModal from '@/components/user/PreferencesModal.vue';
import { preferences } from '@/components/userPreferences';
import { mapStores, mapActions } from 'pinia'
import { useCountyStore } from '@/common/stores/countyStore';
import PrecinctListComponent from './precincts/PrecinctListComponent.vue';
import ContactSearchDropdownComponent from '@/components/ContactSearchDropdownComponent.vue';
import { useSettingsStore } from '@/common/stores/settingsStore';
import { BNavItemDropdown, BDropdownItem } from 'bootstrap-vue-next';

export default {
    name: 'NavBar',
    components: {
        PreferencesModal,
        PrecinctListComponent,
        ContactSearchDropdownComponent,
        BNavItemDropdown,
        BDropdownItem
    },
    emits: ['logout'],
    props: ['currentUserRoles', 'contactId', 'currentUserPreferences', 'auth'],
    data() {
        return {
            router: useRouter(),
            isLoggedIn: false,
            preferences: preferences,
            user: null,
            navLightVisible: false,
            voterSearch: '',
            voterSearchResults: [],
            hasIncompleteForms: false,
        };
    },
    computed: {
        ...mapStores(useCountyStore),
        ...mapStores(useSettingsStore), // Add settings store
    },
    mounted() {
        this.initializeData();
        // runs after firebase is initialized
        onAuthStateChanged(this.auth, async (u) => {
            this.user = u;
            if (u) {
                this.isLoggedIn = true; // if we have a user
                createRolesDocument(u);
                await this.checkIncompleteForms();
                this.setupFormListeners(); // Set up listeners for form changes
            } else {
                this.isLoggedIn = false; // if we do not
            }
        });
    },
    methods: {
        clickSignOut() {
            clickSignOut(this.auth, this.router);
            this.$emit('logout');
        },
        signInWithGoogle() {
            console.log(this)
            signInWithGoogle(this.auth);
        },
        handleNavLinkClick() {
            if (window.innerWidth < 768) {
                this.navLightVisible = false;
            }
        },
        setupFormListeners() {
            if (!this.user) return;

            const db = window.db;
            const userId = this.user.uid;

            const formsToCheck = [
                'memberContactPreferenceForms',
                'memberPriorityForms',
                'memberVolunteerForms',
            ];

            formsToCheck.forEach((collection) => {
                const docRef = doc(db, collection, userId);
                onSnapshot(docRef, () => {
                    this.checkIncompleteForms(); // Recheck forms on document change
                });
            });
        },
        async checkIncompleteForms() {
            if (!this.user) return;

            const db = window.db;
            const userId = this.user.uid;
            const thresholdDate = new Date();
            thresholdDate.setDate(thresholdDate.getDate() - this.settingsStore.formUpdateThresholdDays); // Use global setting

            const formsToCheck = [
                { collection: 'memberContactPreferenceForms' },
                { collection: 'memberPriorityForms' },
                { collection: 'memberVolunteerForms' },
            ];

            for (const form of formsToCheck) {
                const docRef = doc(db, form.collection, userId);
                const docSnap = await getDoc(docRef);

                if (!docSnap.exists()) {
                    this.hasIncompleteForms = true;
                    return;
                }

                const formTimestamp = docSnap.data().updatedAt?.toDate();
                if (!formTimestamp || formTimestamp < thresholdDate) {
                    this.hasIncompleteForms = true;
                    return;
                }
            }

            this.hasIncompleteForms = false;
        },
        showPreferencesModal() {
            // Find and show the preferences modal
            this.$refs.preferencesModal?.show();
        },
        ...mapActions(useCountyStore, ['setCounty', 'initializeData'])
    }
}
</script>
